import React from 'react';
import { Link } from 'react-router-dom';

/** 格式化日期 */
function formatDate(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** 状态样式映射 */
function getApplicationStatusInfo(status?: string) {
  const s = (status || '').toUpperCase();
  if (s === 'PENDING' || s === 'APPLIED' || s === 'SUBMITTED' || s === 'REVIEWING') {
    return { label: '待审核', color: 'bg-amber-50 text-amber-700 border-amber-200/60', isPending: true };
  }
  if (s === 'ACCEPTED' || s === 'APPROVED' || s === 'PASSED' || s === 'SUCCESS') {
    return { label: '已录取', color: 'bg-emerald-50 text-emerald-700 border-emerald-200/60', isPending: false };
  }
  if (s === 'REJECTED' || s === 'FAILED') {
    return { label: '未通过', color: 'bg-red-50 text-red-600 border-red-200/60', isPending: false };
  }
  if (s === 'WITHDRAWN') {
    return { label: '已撤回', color: 'bg-slate-100 text-slate-500 border-slate-200/60', isPending: false };
  }
  return { label: status || '待处理', color: 'bg-blue-50 text-blue-700 border-blue-200/60', isPending: false };
}

export interface JobApplicationListModuleProps {
  applications: Api.Resume.ApplicationResponse[];
  appsLoading: boolean;
  appsPage: number;
  appsTotal: number;
  appsPageSize: number;
  onLoadPage: (page: number) => void;
  onViewDetail: (id?: string) => void;
  onWithdraw: (id?: string) => void;
}

export const JobApplicationListModule: React.FC<JobApplicationListModuleProps> = ({
  applications,
  appsLoading,
  appsPage,
  appsTotal,
  appsPageSize,
  onLoadPage,
  onViewDetail,
  onWithdraw,
}) => {
  const totalPages = Math.ceil(appsTotal / appsPageSize) || 1;

  if (appsLoading) {
    return (
      <div className="glass-card rounded-2xl p-12 text-center">
        <div className="inline-block w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-sm text-foreground-500">正在加载您的求职投递记录...</p>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className="glass-card rounded-2xl p-12 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 text-foreground-400 flex items-center justify-center mx-auto mb-4 text-2xl">
          <i className="ri-send-plane-line"></i>
        </div>
        <h3 className="font-heading font-bold text-lg text-foreground-950 mb-2">暂无求职投递记录</h3>
        <p className="text-xs text-foreground-500 mb-6 max-w-sm mx-auto">
          完善求职资料后，可在招聘大厅浏览优质职位并一键发起投递。
        </p>
        <Link
          to="/demands"
          className="px-6 py-2.5 rounded-xl bg-primary-500 text-white text-xs font-semibold hover:brightness-95 transition-all inline-flex items-center gap-1.5"
        >
          <i className="ri-search-line"></i> 前往招聘大厅
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {applications.map((app) => {
        const statusInfo = getApplicationStatusInfo(app.status);
        return (
          <div
            key={app.id}
            className="glass-card rounded-2xl p-5 md:p-6 transition-all hover:border-primary-200 hover:shadow-xs space-y-4"
          >
            {/* 头部信息 */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusInfo.color}`}>
                  {statusInfo.label}
                </span>
                <span className="text-xs font-mono text-foreground-400">申请单号：{app.id}</span>
              </div>

              <span className="text-xs text-foreground-400">
                投递时间：{formatDate(app.applied_at)}
              </span>
            </div>

            {/* 卡片主内容 */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <p className="text-xs text-foreground-500 flex items-center gap-1.5">
                  <i className="ri-briefcase-line text-primary-500"></i>
                  关联岗位标识：
                  {app.job_posting_id ? (
                    <Link
                      to={`/jobs/${app.job_posting_id}`}
                      className="font-semibold text-primary-600 hover:underline font-mono"
                    >
                      {app.job_posting_id}
                    </Link>
                  ) : (
                    <span className="text-foreground-400">系统内部岗位</span>
                  )}
                </p>

                {app.withdrawn_at && (
                  <p className="text-xs text-foreground-400">
                    撤回时间：{formatDate(app.withdrawn_at)}
                  </p>
                )}
              </div>

              {/* 动作区 */}
              <div className="flex items-center gap-3 shrink-0">
                {app.job_posting_id ? (
                  <Link
                    to={`/jobs/${app.job_posting_id}`}
                    className="px-4 py-2 rounded-xl border border-gray-200 text-foreground-700 text-xs font-medium hover:bg-gray-50 transition-colors cursor-pointer inline-flex items-center gap-1"
                  >
                    查看详情
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => onViewDetail(app.job_posting_id || app.id)}
                    className="px-4 py-2 rounded-xl border border-gray-200 text-foreground-700 text-xs font-medium hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    查看详情
                  </button>
                )}

                {statusInfo.isPending && (
                  <button
                    type="button"
                    onClick={() => onWithdraw(app.id)}
                    className="px-4 py-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 text-xs font-medium transition-colors cursor-pointer"
                  >
                    撤回申请
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* 分页控制 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-100 text-xs text-foreground-500">
          <span>共 {appsTotal} 条记录，第 {appsPage} / {totalPages} 页</span>
          <div className="flex gap-2">
            <button
              disabled={appsPage <= 1}
              onClick={() => onLoadPage(appsPage - 1)}
              className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
            >
              上一页
            </button>
            <button
              disabled={appsPage >= totalPages}
              onClick={() => onLoadPage(appsPage + 1)}
              className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
            >
              下一页
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobApplicationListModule;
